import { ZapFastIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DatasetRuleButton } from '@/Pages/Dataset/DatasetRulesPane';
import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import { DeleteModal } from '@/components/Delete';
import { RuleLogsPane } from '@/components/Rules/RuleLogsPane';
import { useCanAlignEvaluators } from '@/hooks/useCanAlignEvaluators';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useOrganizationId, useRunRules } from '@/hooks/useSwr';
import { RuleSchema } from '@/types/schema';
import {
  DATASET_EVALUTOR_DOCS,
  SESSION_RULES_DOCS,
  apiFeedbackFormulasPath,
  apiRunsPath,
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

import EmptyState from '../EmptyState';
import { DatasetRulesDropdown } from '../EvaluatorPopover/EvaluatorsPopover';
import { getEvaluatorsColumnsDef } from './components/Columns';
import { useEvaluatorsTable } from './hooks/useEvaluatorsTable';
import { CompositeScoreRule } from './utils/convertFeedbackFormulaToRule';

export const DatasetEvaluatorsTable = ({
  onStartAlignEvaluator,
}: {
  onStartAlignEvaluator?: () => void;
}) => {
  const { datasetId } = useParams();

  const {
    data: allRules,
    mutate,
    isLoading,
    isValidating,
  } = useRunRules({
    dataset_id: datasetId,
  });

  const [ruleLogsPaneRule, setRuleLogsPaneRule] = useState<
    RuleSchema | undefined
  >(undefined);

  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const onRowClick = (row: RuleSchema) => {
    navigate(
      `/${appOrganizationPath}/${organizationId}/datasets/${datasetId}/${appEvaluatorPath}/${row.id}`
    );
  };

  const combinedMutate = () => {
    mutate();
  };

  if (!allRules && isLoading) {
    return <LinearProgress />;
  }

  if (!datasetId) {
    return <div>Dataset ID is required</div>;
  }

  return (
    <>
      <EvaluatorsTable
        rules={allRules ?? []}
        isLoading={isLoading}
        isValidating={isValidating}
        mutate={combinedMutate}
        setRuleLogsPaneRule={setRuleLogsPaneRule}
        onRowClick={onRowClick}
        emptyState={
          <EmptyState
            title="Create your first evaluator"
            description={
              'There are no evaluators configured on this dataset. Evaluators let you evaluate experiment results'
            }
            action={
              <DatasetRulesDropdown
                trigger={
                  <DatasetRuleButton
                    datasetId={datasetId}
                    onClick={() => void 0}
                    variant="dataset"
                  />
                }
                datasetId={datasetId}
                checkable={false}
                hideCurrentRules={true}
                onStartAlignEvaluator={onStartAlignEvaluator}
              />
            }
            docsLink={DATASET_EVALUTOR_DOCS}
            fancyIcon={false}
            icon={<ZapFastIcon />}
            className="mt-12"
            size="md"
          />
        }
      />

      <RuleLogsPane
        rule={ruleLogsPaneRule}
        open={!!ruleLogsPaneRule}
        onClose={() => setRuleLogsPaneRule(undefined)}
      />
    </>
  );
};

export const SessionEvaluatorsTable = (props: {
  createEvaluatorAction: React.ReactNode;
}) => {
  const { sessionId } = useParams();
  const {
    data: allRules,
    mutate,
    isLoading,
    isValidating,
  } = useRunRules({
    session_id: sessionId,
  });

  const evaluatorRules = useMemo(() => {
    return allRules?.filter(
      (rule) =>
        (rule.evaluators?.length ?? 0) > 0 ||
        (rule.code_evaluators?.length ?? 0) > 0
    );
  }, [allRules]);

  const [ruleLogsPaneRule, setRuleLogsPaneRule] = useState<
    RuleSchema | undefined
  >(undefined);

  const navigate = useNavigate();
  const organizationId = useOrganizationId();

  const onRowClick = (row: RuleSchema) => {
    navigate(
      `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}/${appEvaluatorPath}/${row.id}`
    );
  };

  const combinedMutate = () => {
    mutate();
  };

  if (!allRules && isLoading) {
    return <LinearProgress />;
  }

  if (!sessionId) {
    return <div>Session ID is required</div>;
  }

  return (
    <>
      <EvaluatorsTable
        rules={evaluatorRules ?? []}
        isLoading={isLoading}
        isValidating={isValidating}
        mutate={combinedMutate}
        onRowClick={onRowClick}
        setRuleLogsPaneRule={setRuleLogsPaneRule}
        emptyState={
          <EmptyState
            title="Create your first evaluator"
            description={
              'There are no online evaluators configured on this project. Online evaluations let you monitor app performance, track improvements, and maintain quality over time.'
            }
            action={props.createEvaluatorAction}
            docsLink={SESSION_RULES_DOCS}
            fancyIcon={false}
            icon={<ZapFastIcon />}
            className="mt-12"
            size="md"
          />
        }
      />
      <RuleLogsPane
        rule={ruleLogsPaneRule}
        open={!!ruleLogsPaneRule}
        onClose={() => setRuleLogsPaneRule(undefined)}
      />
    </>
  );
};

export const EvaluatorsTable = (props: {
  rules: (RuleSchema | CompositeScoreRule)[];
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
  setRuleLogsPaneRule: Dispatch<SetStateAction<RuleSchema | undefined>>;
  onRowClick: (row: RuleSchema) => void;

  emptyState: React.ReactNode;
}) => {
  'use no memo';
  const { paginationModel, setPaginationModel } = useDataGridState({});
  const [deleteOpen, setDeleteOpen] = useState<string | null>(null);
  const [deleteResource, setDeleteResource] = useState<
    'rules' | 'formulas' | null
  >(null);

  // Implement client-side pagination since the API doesn't support pagination
  // TODO: implement pagination in the API
  const paginatedRules = useMemo(() => {
    if (!props.rules) return undefined;

    const startIndex = paginationModel.pageIndex * paginationModel.pageSize;
    const endIndex = startIndex + paginationModel.pageSize;
    const slicedRules = props.rules.slice(startIndex, endIndex);

    // Add headers property for pagination information
    return Object.assign(slicedRules, {
      headers: {
        'x-pagination-total': props.rules.length,
      },
    });
  }, [props.rules, paginationModel.pageIndex, paginationModel.pageSize]);

  const totalCount = props.rules?.length ?? 0;

  const organizationId = useOrganizationId();
  const { areEvaluatorsAlignable } = useCanAlignEvaluators();

  const columns = useMemo(() => {
    return getEvaluatorsColumnsDef({
      organizationId,
      onOpenRuleLogs: props.setRuleLogsPaneRule,
      areEvaluatorsAlignable,
    });
  }, [organizationId, props.setRuleLogsPaneRule, areEvaluatorsAlignable]);

  const handleDelete = useCallback(
    (id: string, resource: 'rules' | 'formulas') => {
      setDeleteOpen(id);
      setDeleteResource(resource);
    },
    []
  );

  const table = useEvaluatorsTable({
    rows: paginatedRules,
    paginationModel,
    onPaginationChange: setPaginationModel,
    columns,
    setDeleteOpen: handleDelete,
  });

  if (props.rules.length === 0) {
    return props.emptyState;
  }

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-secondary">
        <DataGrid
          table={table}
          cellHeight={56}
          isLoading={props.isLoading || props.isValidating}
          emptyState={
            <div className="text-tertiary">There are no evaluators yet</div>
          }
          cellSpacingVariant="wide"
          onClick={props.onRowClick}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-tertiary">
          {totalCount > 0 &&
            `${totalCount} evaluator${totalCount === 1 ? '' : 's'} in total`}
        </div>
        <DataGridPagination table={table} />
      </div>

      <DeleteModal
        endpoint={
          deleteResource === 'rules'
            ? `${apiRunsPath}/rules`
            : `${apiFeedbackFormulasPath}`
        }
        id={deleteOpen as string}
        isOpen={!!deleteOpen}
        doClose={() => {
          props.mutate();
          setDeleteOpen(null);
          setDeleteResource(null);
        }}
        invalidationPrefixes={[
          `${apiRunsPath}/rules`,
          `${apiFeedbackFormulasPath}`,
        ]}
        modalText="Are you sure you want to delete this evaluator?"
      />
    </>
  );
};
